import { Button, Modal } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { IPomReportSubCategoryData } from '../util/DataModelInterface';
import { Fragment, useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import { format } from 'date-fns';
import { formatFinNumber, getPeriodDate } from '../util/FormatDateUtil';
import { ToastWrap } from './ToastWrap';
import { CalendarInput } from './CalendarInput';

export interface ISubcategoryModalProps {
  isAddNew: boolean;
  active: boolean;
  modalId: string;
  onClose: () => void;
  title: string;
  data: IPomReportSubCategoryData;
  confirmbutton: ISubcategoryModalButtonProps;
}

export interface ISubcategoryModalButtonProps {
  name: string;
  class: string;
  onClick: (d: any) => void;
}

const popperModifiers = [
  {
    name: "offset",
    options: {
      offset: [0, 0],
    },
  }
];

export const SubcategoryModal  = (props: ISubcategoryModalProps) => {
  const intl = useIntl();

  const [toastActive, setToastActive] = useState(false);
  const [toastHeader, setToastHeader] = useState("");
  const [toastText, setToastText] = useState("");
  const [toastType, setToastType] = useState("");

  const [subcat, setSubcat] = useState({...props.data});
  const [subcatState, setSubcatState] = useState({...props.data});
  const [selectedStartDay, setSelectedStartDay] = useState(getPeriodDate(""));
  const [newStart, setNewStart] = useState("");
  const [newPrice, setNewPrice] = useState<string>("");
  const [addNewPrice, setAddNewPrice] = useState(false);
  const [isFieldsEdited, setIsFieldsEdited] = useState(false);
  const lang = intl.locale;

  useEffect(() => {
    if (props.active) {
      setSubcat({...props.data});
      setSubcatState({...props.data});
      setSelectedStartDay(getPeriodDate(""));
      setNewStart("");
      setNewPrice("");
      setAddNewPrice(false);
      setIsFieldsEdited(false);
    }
  }, [props.active, props.data]);

  useEffect(() => {
    if (newStart && newPrice) {
      let priceObj = {...subcat.price};
      let priceText = newPrice.replace(/[−–—]/g, '-').replace(/[^0-9,.-]/g, '').replace(/(?!^)-/g, '').replace(',', '.');
      priceObj[newStart] = priceText;
      setSubcatState({...subcatState, price: priceObj});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newPrice, newStart]);

  const updateSubCat = (event: { currentTarget: any; }) => {
    const target = event.currentTarget;

    setSubcat({
      ...subcat,
      [target.name]: target.type === 'checkbox'
          ? target.checked
          : target.value
    });
    setSubcatState({
      ...subcatState,
      [target.name]: target.type === 'checkbox'
          ? target.checked
          : target.value
    });
  }

  const updateUnit = (unit: string) => {
    setSubcat({...subcat, unit: unit});
    setSubcatState({...subcatState, unit: unit});
  }

//   "price": {
//     "2018-12":"0.200",
//     "2022-01":"0.150"
//    },

  const updatePrice = (event: { currentTarget: any; }) => {
    const target = event.currentTarget;
    const price = target.value;
    setNewPrice(price);
  }

  const modifyPrice = (value: any) => {
    // Typing done -> modify value to number now... (remove other character also)
    let modValue = formatFinNumber(value.replace(/[−–—]/g, '-').replace(/[^0-9,.-]/g, '').replace(/(?!^)-/g, '').replace(',', '.'), 3).replace(/[−–—]/g, '-');
    setNewPrice(modValue);
  }

  const handleSelect = () => {
    const isValidForm = Object
          .values(validationRules)
          .every(key => key);

    if (!isValidForm) {
      setIsFieldsEdited(true);
      // console.log('Not valid form');
      setToastHeader(intl.formatMessage({id: 'error.subcategories.add'}));
      setToastText(intl.formatMessage({id: 'companies.fail.info'}));
      setToastType('warning');
      setToastActive(true);
      return;
    }
    setIsFieldsEdited(false);
    props.confirmbutton.onClick(subcatState);
    handleClose();
  }

  const handleClose = () => {
    props.onClose();
  }

  const validationRules: {[x:string]: boolean} = {
    code: !!subcatState.code && subcatState.code.match(/^ *$/) === null,
    start: (!!newStart && newStart.match(/^ *$/) === null) || (!addNewPrice && !props.isAddNew),
    price: (!!newPrice && newPrice.match(/^ *$/) === null) || (!addNewPrice && !props.isAddNew),
    unit: !!subcatState.unit && subcatState.unit.match(/^ *$/) === null,
    name_fi: !!subcatState.name_fi && subcatState.name_fi.match(/^ *$/) === null,
    name_se: !!subcatState.name_se && subcatState.name_se.match(/^ *$/) === null,
    name_en: !!subcatState.name_en && subcatState.name_en.match(/^ *$/) === null,
  }

  const validate = (field: string) => {
    return ((isFieldsEdited && !validationRules[field]) ? 'has-error' : '')
  }

  const handleKeyDown = (e: any) => {
    if (e.key === 'Enter') {
        // console.log('handleEnter - value %s name %s', e.target.value, e.target.name);
        modifyPrice(e.target.value);
    }
  }

const handleBlur = (e: any) => {
    // console.log('handleBlur - value %s name %s', e.target.value, e.target.name);
    if (e.target.value) {
      modifyPrice(e.target.value);
    }
  }

  const getEditPriceRow = () => {
    let priceText  = (typeof newPrice === 'number' && newPrice !== 0) ? formatFinNumber(newPrice, 3) : newPrice;
    return (
      <>
        <tr>
          <td className="align-top">
            <DatePicker
              dateFormat="yyyy-MM"
              showPopperArrow={false}
              selected={selectedStartDay}
              onChange={(date) => {
                if (date) {
                  setSelectedStartDay(date);
                  setNewStart(format(date, 'yyyy-MM'));
                }
              }}
              customInput={<CalendarInput label='categories.subcategory.pricestart' hideLabel={true}/>}
              name="startdate"
              id="startdate"
              showMonthYearPicker
              popperModifiers={popperModifiers}
              locale={lang}
            />
          </td>
          <td className="align-top">
            <div className={"form-group " + validate('price')}>
              <label htmlFor="price" className="form-label sr-only"><FormattedMessage id='categories.subcategory.price'/>:</label>
              <div className="input-group">
                <input id="price" name="price" className="form-control" type="text" aria-required="true"
                  autoComplete="off" onChange={updatePrice} onKeyDown={handleKeyDown} onBlur={handleBlur}
                  value={priceText}/>
                <div className="input-group-append">
                  <span className="input-group-text">€</span>
                </div>
              </div>
              <div aria-atomic="true" aria-live="polite">
                <div className="field-error" id="test1" >
                  <i className="fa fa-warning mr-2"></i>
                  <FormattedMessage id='categories.obligatory.info'/>
                </div>
              </div>
            </div>
          </td>
        </tr>
      </>
    )
  }

  const getPriceRow = (start: string, price: string, index: number) => {
    let priceText = formatFinNumber(price, 3);
    return (
      <Fragment key={index}>
        <tr>
          <td className="align-top">
            <div className="form-group">
              <label htmlFor="startdate" className="form-label sr-only"><FormattedMessage id='categories.subcategory.pricestart'/>:</label>
              <div className="input-group">
                <input className="form-control datepicker" disabled type="text" aria-required="true"
                  name="startdate" id="startdate" value={start}/>
                <button className="ui-datepicker-trigger">
                  <i className='fa fa-calendar'></i>
                  <span className='sr-only'><FormattedMessage id='button.open.calendar'/></span>
                </button>
              </div>
              {/* <div aria-atomic="true" aria-live="polite">
                <div className="field-error" id="test1" >
                  <i className="fa fa-warning mr-2"></i>
                  <FormattedMessage id='categories.obligatory.info'/>
                </div>
              </div> */}
            </div>
          </td>
          <td className="align-top">
            <div className="form-group">
              <label htmlFor="price" className="form-label sr-only"><FormattedMessage id='categories.subcategory.price'/>:</label>
              <div className="input-group">
                <input id="price" name="price" className="form-control" type="text" aria-required="true"
                  disabled value={priceText}/>
                <div className="input-group-append">
                  <span className="input-group-text">€</span>
                </div>
              </div>
              {/* <div aria-atomic="true" aria-live="polite">
                <div className="field-error" id="test1" >
                  <i className="fa fa-warning mr-2"></i>
                  <FormattedMessage id='categories.obligatory.info'/>
                </div>
              </div> */}
            </div>
          </td>
        </tr>
      </Fragment>
    )
  }

  if (!props.data) {
    return null;
  }

  return (
    <>
      <ToastWrap active={toastActive} heading={toastHeader} text={toastText} type={toastType} setActive={(active: boolean) => setToastActive(active)} />
      <Modal id={props.modalId} show={props.active} onHide={handleClose} dialogClassName='wide-modal'>

      <Modal.Header>
        <h2>{props.title}</h2>
        <button type="button" className="btn btn-link p-3" onClick={handleClose} aria-label={ intl.formatMessage({ id: 'button.close' })}>
          <i aria-hidden="true" role="presentation" className="fa fa-remove"></i>
        </button>
      </Modal.Header>

      <Modal.Body>

        {/* {props.isAddNew &&
          <div className="form-group">
            <label htmlFor="productcat" className="form-label"><FormattedMessage id="categories.category.select"/></label>
            <div className="select-group">
              <select id="productcat" name="productcat" className="form-control form-select"
                onChange={updateCategory}
                value={selectedCategory ? selectedCategory : ''}>
                <option value="0"></option>
                {props.categories.map((cat: string, index: number) => {
                  return <option key={index} value={cat}>{cat}</option>
                })}
              </select>
            </div>
          </div>
        } */}
        <p className="details-info">
          <span className="key"><FormattedMessage id="categories.category"/></span>
          <span className="value">{props.data.category} {props.data.category_title}</span>
        </p>

        <div className="productdetails d-flex flex-md-row flex-column">
        <div className="highlight_bg flex-fill">

          {props.isAddNew &&
            <div className={"form-group " + validate('code')}>
              <label htmlFor="code" className="form-label"><FormattedMessage id='categories.subcategory.code'/></label>
              <input id="code" name="code" className="form-control" type="text"
                autoComplete="off" onChange={updateSubCat}
                value={subcat.code ? subcat.code : ""}/>
            </div>
          }

          {!props.isAddNew &&
          <p className="details-info">
            <span className="form-label"><FormattedMessage id="categories.subcategory.code"/></span>
            <span className="value">{props.data.code}</span>
          </p>
          }


          <fieldset className="form-group checkbox-group">
            <legend className="form-label"><FormattedMessage id='categories.subcategory.columns'/></legend>
            <div aria-atomic="true" aria-live="polite"><div id="fielderror4" className="field-error"><i className="fa fa-warning me-2"></i>Pakollinen tieto</div></div>
            <div className="form-check checkboxgroup">
              <label htmlFor="type1">
                <input type="checkbox" className="form-check-input" id="type1" name="fin" value="1"
                  checked={subcat.fin === true} onChange={updateSubCat}/>
                <span className="label-text"><FormattedMessage id='categories.subcategory.fin'/></span>
              </label>
            </div>
            <div className="form-check checkboxgroup">
              <label htmlFor="type2">
                <input type="checkbox" className="form-check-input" id="type2" name="aland" value="2"
                  checked={subcat.aland === true} onChange={updateSubCat}/>
                <span className="label-text"><FormattedMessage id='categories.subcategory.aland'/></span>
              </label>
            </div>
            <div className="form-check checkboxgroup">
              <label htmlFor="type3">
                <input type="checkbox" className="form-check-input" id="type3" name="b2b" value="3"
                  checked={subcat.b2b === true} onChange={updateSubCat}/>
                <span className="label-text"><FormattedMessage id='categories.subcategory.b2b'/></span>
              </label>
            </div>
          </fieldset>

          <fieldset className={"form-group " + validate('unit')}>
            <legend className="form-label"><FormattedMessage id='categories.subcategory.priceunit'/></legend>
            <div aria-atomic="true" aria-live="polite"><div id="fielderror4" className="field-error"><i className="fa fa-warning me-2"></i>Pakollinen tieto</div></div>
            <div className="radiogroup form-check">
              <label className="form-check-label"  htmlFor="prizeunit1">
              <input id="prizeunit1" name="prizeunit" type="radio" className="form-check-input"
                value="kg" onChange={() => updateUnit("kg")} checked={subcat.unit === "kg"}/>
              <span className="label-text">kg</span></label>
            </div>
            <div className="radiogroup form-check">
              <label className="form-check-label"  htmlFor="prizetype2">
              <input id="prizeunit2" name="prizeunit" type="radio" className="form-check-input"
                value="kpl" onChange={() => updateUnit("kpl")} checked={subcat.unit === "kpl"}/>
              <span className="label-text"><FormattedMessage id='unit.pieces'/></span></label>
            </div>
          </fieldset>

          <div className="tbl-wrp">
            <table className="table">
              <thead>
                <tr>
                  <th className="subcat"><FormattedMessage id='categories.subcategory.pricestart'/></th>
                  <th className="subcat"><FormattedMessage id='categories.subcategory.price'/></th>
                </tr>
              </thead>
              <tbody>
                {props.data?.price && Object.keys(props.data.price).map((k, ind) => {
                  return getPriceRow(k, props.data.price[k], ind);
                })}

                {(addNewPrice || props.isAddNew) && getEditPriceRow()}

              </tbody>
            </table>

            {!addNewPrice && !props.isAddNew &&
              <div className="d-flex flex-row my-4">
                <div className="ms-auto">
                  <button type="button" className="btn btn-link add-rows-to-table" onClick={() => setAddNewPrice(true)}>
                    <i className="fa fa-plus mr-2"></i><FormattedMessage id='categories.subcategory.addrow'/>
                  </button>
                </div>
              </div>
            }

          </div>

        </div>

        <div className="flex-fill ms-md-4 ps-md-4 ">
          <h4><FormattedMessage id='categories.subcategory.texts'/></h4>
          <fieldset>
            <legend><FormattedMessage id='languages.finnish'/></legend>
            <div className={"form-group " + validate('name_fi')}>
              <label htmlFor="name_fi" className="form-label"><FormattedMessage id='categories.subcategory.name'/></label>
              <input id="name_fi" type="text" name="name_fi" className="form-control"
                autoComplete="off" onChange={updateSubCat}
                value={subcat.name_fi ? subcat.name_fi : ""}/>
            </div>
            <div className="form-group">
              <label htmlFor="description_fi" className="form-label"><FormattedMessage id='categories.subcategory.info'/></label>
              <textarea id="description_fi" name="description_fi" className="form-control"
                autoComplete="off" onChange={updateSubCat}
                value={subcat.description_fi ? subcat.description_fi : ""}></textarea>
            </div>
          </fieldset>
          <fieldset>
            <legend><FormattedMessage id='languages.swedish'/></legend>
            <div className={"form-group " + validate('name_se')}>
              <label htmlFor="name_se" className="form-label"><FormattedMessage id='categories.subcategory.name'/></label>
              <input id="name_se" type="text" name="name_se" className="form-control"
                autoComplete="off" onChange={updateSubCat}
                value={subcat.name_se ? subcat.name_se : ""}/>
            </div>
            <div className="form-group">
              <label htmlFor="description_se" className="form-label"><FormattedMessage id='categories.subcategory.info'/></label>
              <textarea id="description_se" name="description_se" className="form-control"
                autoComplete="off" onChange={updateSubCat}
                value={subcat.description_se ? subcat.description_se : ""}></textarea>
            </div>
          </fieldset>

          <fieldset>
            <legend><FormattedMessage id='languages.english'/></legend>
            <div className={"form-group " + validate('name_en')}>
              <label htmlFor="name_en" className="form-label"><FormattedMessage id='categories.subcategory.name'/></label>
              <input id="name_en" type="text" name="name_en" className="form-control"
                autoComplete="off" onChange={updateSubCat}
                value={subcat.name_en ? subcat.name_en : ""}/>
            </div>
            <div className="form-group">
              <label htmlFor="description_en" className="form-label"><FormattedMessage id='categories.subcategory.info'/></label>
              <textarea id="description_en" name="description_en" className="form-control"
                autoComplete="off" onChange={updateSubCat}
                value={subcat.description_en ? subcat.description_en : ""}></textarea>
            </div>
          </fieldset>

        </div>
        </div>


      </Modal.Body>

      <Modal.Footer>
        
        <Button className="btn btn-secondary" onClick={handleClose}>
          <FormattedMessage id="button.close"/>
        </Button>
        <Button className={props.confirmbutton.class} onClick={handleSelect}>
          {props.confirmbutton.name}
        </Button>
      </Modal.Footer>
      </Modal>

    </>
  );
}
